import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { TagGroup, TagList, Tag, TooltipTrigger } from "react-aria-components";
import { useResizeObserver } from "@reactuses/core";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { Tooltip } from "../basic/Tooltip";
import { Button } from "../basic/Button";
import { defaultTagColor } from "../basic/Tag";
import type { TagsEnhancedItem } from "../../../zodios/common/tagsSchema";
import { useModuleEnabled } from "../../../redux/selectors/modules";

type TagItem = Pick<TagsEnhancedItem, "id" | "name" | "color">;

const tagStyle = css({
  flexShrink: 0,
  borderRadius: "full",
  paddingY: "xxsmall",
  paddingX: "small",
  color: "white",
  fontSize: "small",
  fontWeight: 600,
  backgroundColor: "var(--tag-color)",
  whiteSpace: "nowrap",
});

interface OverflowTagsProps {
  id: string | number;
  tags?: string[];
  enhancedTags?: TagsEnhancedItem[];
}

export function OverflowTags({ tags, enhancedTags, id }: OverflowTagsProps) {
  const { t } = useTranslation(["common"]);
  const measureRef = useRef<HTMLSpanElement>(null);
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const items: TagItem[] = useMemo(
    () =>
      tagsEnhancedEnabled && enhancedTags?.length ?
        enhancedTags
      : (tags?.map((t, i) => ({ id: i, name: t, color: defaultTagColor })) ?? []),
    [tagsEnhancedEnabled, enhancedTags, tags],
  );

  const [visibleCount, setVisibleCount] = useState(items.length);

  const measure = useCallback(() => {
    const container = measureRef.current;
    if (!container || !items.length) return;
    setVisibleCount(items.length);

    const children = container.children;
    if (children.length === 0) return;

    const firstTop = (children[0] as HTMLElement).offsetTop;
    let firstRowCount = 0;
    for (const child of children) {
      if ((child as HTMLElement).offsetTop > firstTop) break;
      firstRowCount++;
    }

    const allFit = firstRowCount === children.length;
    setVisibleCount(allFit ? items.length : Math.max(firstRowCount - 1, 1));
  }, [items]);

  useLayoutEffect(measure, [measure]);
  useResizeObserver(measureRef, measure);

  if (!items.length) return null;

  const hiddenCount = items.length - visibleCount;
  const visibleItems = items.slice(0, visibleCount);

  return (
    <>
      <span
        ref={measureRef}
        aria-hidden
        className={css({
          display: "flex",
          flexWrap: "wrap",
          gap: "small",
          visibility: "hidden",
          height: 0,
          overflow: "hidden",
          pointerEvents: "none",
        })}
      >
        {items.map((item) => (
          <span
            className={tagStyle}
            style={{ "--tag-color": item.color || defaultTagColor } as React.CSSProperties}
            key={`${id}-${item.id}-measure`}
          >
            {item.name}
          </span>
        ))}
        <span className={tagStyle} style={{ "--tag-color": defaultTagColor } as React.CSSProperties}>
          +{items.length}
        </span>
      </span>
      <span className={css({ display: "inline-flex", alignItems: "center", gap: "small" })}>
        <TagGroup aria-label={t(($) => $.common.tags.Tags)}>
          <TagList items={visibleItems} className={css({ display: "flex", gap: "small" })}>
            {(item) => (
              <Tag
                id={`${id}-${item.id}`}
                textValue={item.name}
                className={tagStyle}
                style={{ "--tag-color": item.color || defaultTagColor } as React.CSSProperties}
              >
                {item.name}
              </Tag>
            )}
          </TagList>
        </TagGroup>
        {hiddenCount > 0 && (
          <TooltipTrigger delay={0} closeDelay={0}>
            <Button variant="contentOnly" className={css({ flexShrink: 0 })}>
              <span className={tagStyle} style={{ "--tag-color": defaultTagColor } as React.CSSProperties}>
                +{hiddenCount}
              </span>
            </Button>
            <Tooltip>
              <span
                className={css({
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "xsmall",
                })}
              >
                {items.map((item) => (
                  <span
                    className={tagStyle}
                    style={{ "--tag-color": item.color || defaultTagColor } as React.CSSProperties}
                    key={`${id}-${item.id}-tooltip`}
                  >
                    {item.name}
                  </span>
                ))}
              </span>
            </Tooltip>
          </TooltipTrigger>
        )}
      </span>
    </>
  );
}
